import React, { useContext, useEffect, useState } from "react";
import { AppContext } from "../context/AppContext";
import "bootstrap/dist/css/bootstrap.min.css";
import Loader from "./Loader";
import {
  getWithAuthCallWithErrorResponse,
  postWithAuthCall,
} from "../api/ApiServices";
import ApiConfig from "../api/ApiConfig";
import { ToastMsg } from "./ToastMsg";

// Admin editor for the dynamic filter configuration that drives the mobile
// filter screen AND the profile/onboarding pickers. Each field can be shown or
// hidden, relabelled, switched single<->multi select, given its options
// (add/remove/reorder), and range fields get min/max/step. Reset restores the
// built-in defaults (which reproduce the app's original hardcoded lists).
const GRADIENT = "linear-gradient(90deg, #055D9D 0%, #7A2E8E 55%, #E40288 100%)";

const move = (arr, i, dir) => {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return arr;
  const next = arr.slice();
  [next[i], next[j]] = [next[j], next[i]];
  return next;
};

// ---- length units (height): min/max are always STORED in cm; the unit only
// controls how the admin enters them and how the mobile renders them. ----
const CM_PER_IN = 2.54;
const HEIGHT_UNITS = [
  { value: "ft_in", label: "Feet & Inches" },
  { value: "in", label: "Inches" },
  { value: "cm", label: "Centimeters" },
];
const isLengthUnit = (u) => ["ft_in", "in", "cm"].includes(u);
const cmToIn = (cm) => cm / CM_PER_IN;
const inToCm = (inches) => Math.round(inches * CM_PER_IN);
const cmToFtIn = (cm) => {
  if (cm == null) return { ft: "", inch: "" };
  const totalIn = Math.round(cm / CM_PER_IN);
  return { ft: Math.floor(totalIn / 12), inch: totalIn % 12 };
};
const ftInToCm = (ft, inch) =>
  Math.round(((Number(ft) || 0) * 12 + (Number(inch) || 0)) * CM_PER_IN);
const previewLabel = (unit, cm) => {
  if (cm == null) return "?";
  if (unit === "cm") return `${Math.round(cm)} cm`;
  if (unit === "in") return `${Math.round(cmToIn(cm))}"`;
  const { ft, inch } = cmToFtIn(cm);
  return `${ft}'${inch}"`;
};

// Range editor. Length fields (height) get a unit dropdown + unit-based bound
// entry; other ranges (age=years, compatibility=%) keep numeric min/max + a
// free-text unit label.
const RangeEditor = ({ field, onPatch }) => {
  const lengthField = isLengthUnit(field.unit);
  const lbl = (t) => (
    <label className="text-muted mb-1 d-block" style={{ fontSize: 11 }}>
      {t}
    </label>
  );
  const numeric = (k) => (
    <div className="col-auto" key={k}>
      {lbl(k)}
      <input
        type="number"
        className="form-control form-control-sm"
        style={{ width: 110 }}
        value={field[k] ?? ""}
        onChange={(e) =>
          onPatch({ [k]: e.target.value === "" ? null : Number(e.target.value) })
        }
      />
    </div>
  );

  if (!lengthField) {
    return (
      <div className="row g-2 mt-2 align-items-end">
        {["min", "max", "step"].map(numeric)}
        <div className="col-auto">
          {lbl("unit")}
          <input
            className="form-control form-control-sm"
            style={{ width: 110 }}
            value={field.unit || ""}
            onChange={(e) => onPatch({ unit: e.target.value })}
          />
        </div>
      </div>
    );
  }

  const unit = field.unit || "ft_in";
  const renderBound = (which) => {
    const cm = field[which];
    if (unit === "cm") {
      return (
        <div className="col-auto" key={which}>
          {lbl(`${which} (cm)`)}
          <input
            type="number"
            className="form-control form-control-sm"
            style={{ width: 110 }}
            value={cm ?? ""}
            onChange={(e) =>
              onPatch({
                [which]:
                  e.target.value === "" ? null : Math.round(Number(e.target.value)),
              })
            }
          />
        </div>
      );
    }
    if (unit === "in") {
      const inches = cm == null ? "" : Math.round(cmToIn(cm));
      return (
        <div className="col-auto" key={which}>
          {lbl(`${which} (in)`)}
          <input
            type="number"
            className="form-control form-control-sm"
            style={{ width: 110 }}
            value={inches}
            onChange={(e) =>
              onPatch({
                [which]: e.target.value === "" ? null : inToCm(Number(e.target.value)),
              })
            }
          />
        </div>
      );
    }
    const { ft, inch } = cmToFtIn(cm);
    return (
      <div className="col-auto" key={which}>
        {lbl(which)}
        <div className="d-flex align-items-center gap-1">
          <input
            type="number"
            className="form-control form-control-sm"
            style={{ width: 64 }}
            placeholder="ft"
            value={ft}
            onChange={(e) => onPatch({ [which]: ftInToCm(e.target.value, inch) })}
          />
          <span className="text-muted">ft</span>
          <input
            type="number"
            className="form-control form-control-sm"
            style={{ width: 64 }}
            placeholder="in"
            min="0"
            max="11"
            value={inch}
            onChange={(e) => onPatch({ [which]: ftInToCm(ft, e.target.value) })}
          />
          <span className="text-muted">in</span>
        </div>
      </div>
    );
  };

  return (
    <div className="mt-2">
      <div className="row g-2 align-items-end">
        <div className="col-auto">
          {lbl("Display unit")}
          <select
            className="form-select form-select-sm"
            style={{ width: 160 }}
            value={unit}
            onChange={(e) => onPatch({ unit: e.target.value })}
          >
            {HEIGHT_UNITS.map((u) => (
              <option key={u.value} value={u.value}>
                {u.label}
              </option>
            ))}
          </select>
        </div>
        {renderBound("min")}
        {renderBound("max")}
        {unit === "cm" && numeric("step")}
      </div>
      <div className="text-muted mt-2" style={{ fontSize: 12 }}>
        Stored as centimetres. Range: {previewLabel(unit, field.min)} –{" "}
        {previewLabel(unit, field.max)} ({field.min ?? "?"}–{field.max ?? "?"} cm)
      </div>
    </div>
  );
};

const FilterSettings = () => {
  const { sidebar } = useContext(AppContext);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = () => {
    setLoading(true);
    getWithAuthCallWithErrorResponse(ApiConfig.FILTER_CONFIG)
      .then((res) => {
        setLoading(false);
        if (res.error) {
          ToastMsg("error", res.json?.message || "Could not load filter config");
          return;
        }
        const list = (res.json?.data?.fields || [])
          .slice()
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        setFields(list);
      })
      .catch(() => {
        setLoading(false);
        ToastMsg("error", "Could not load filter config");
      });
  };

  const save = () => {
    setSaving(true);
    const payload = fields.map((f, i) => ({ ...f, order: i + 1 }));
    postWithAuthCall(ApiConfig.FILTER_CONFIG, JSON.stringify({ fields: payload }))
      .then((res) => {
        setSaving(false);
        if (!res || res.error) {
          ToastMsg("error", (res && res.message) || "Save failed");
          return;
        }
        ToastMsg("success", "Filter settings saved");
        load();
      })
      .catch(() => {
        setSaving(false);
        ToastMsg("error", "Save failed");
      });
  };

  const resetDefaults = () => {
    if (!window.confirm("Reset all filter settings to the built-in defaults?")) {
      return;
    }
    setSaving(true);
    postWithAuthCall(ApiConfig.FILTER_CONFIG_RESET, JSON.stringify({}))
      .then((res) => {
        setSaving(false);
        if (!res || res.error) {
          ToastMsg("error", (res && res.message) || "Reset failed");
          return;
        }
        ToastMsg("success", "Reset to defaults");
        load();
      })
      .catch(() => {
        setSaving(false);
        ToastMsg("error", "Reset failed");
      });
  };

  // ---- field/option mutators (immutable) ----
  const patchField = (idx, patch) =>
    setFields((fs) => fs.map((f, i) => (i === idx ? { ...f, ...patch } : f)));

  const patchOption = (fi, oi, patch) =>
    setFields((fs) =>
      fs.map((f, i) =>
        i === fi
          ? {
              ...f,
              options: f.options.map((o, k) =>
                k === oi ? { ...o, ...patch } : o
              ),
            }
          : f
      )
    );

  const addOption = (fi) =>
    setFields((fs) =>
      fs.map((f, i) =>
        i === fi
          ? { ...f, options: [{ value: "", label: "" }, ...(f.options || [])] }
          : f
      )
    );

  const removeOption = (fi, oi) =>
    setFields((fs) =>
      fs.map((f, i) =>
        i === fi ? { ...f, options: f.options.filter((_, k) => k !== oi) } : f
      )
    );

  const moveOption = (fi, oi, dir) =>
    setFields((fs) =>
      fs.map((f, i) => (i === fi ? { ...f, options: move(f.options, oi, dir) } : f))
    );

  const moveField = (idx, dir) => setFields((fs) => move(fs, idx, dir));

  if (loading) {
    return (
      <main id="cx-main" className={sidebar ? "admin-main" : "cx-active admin-main"}>
        <Loader />
      </main>
    );
  }

  return (
    <main id="cx-main" className={sidebar ? "admin-main" : "cx-active admin-main"}>
      <div className="heading-btn-main">
        <div className="heading-common">
          <label htmlFor="">Filter Settings</label>
        </div>
        <div className="add-btn d-flex">
          <button
            className="btn btn-outline-secondary me-2"
            onClick={resetDefaults}
            disabled={saving}
          >
            Reset to defaults
          </button>
          <button
            className="btn text-white"
            style={{ background: GRADIENT, border: "none" }}
            onClick={save}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      <p className="text-muted small mb-4">
        Controls the mobile filter screen and the profile pickers. Drag order with
        the arrows, toggle where each field appears, switch single/multi select,
        and edit each field's options. Changes apply app-wide after you save.
      </p>

      {fields.map((f, fi) => (
        <FieldCard
          key={f.key}
          field={f}
          index={fi}
          total={fields.length}
          onPatch={(patch) => patchField(fi, patch)}
          onPatchOption={(oi, patch) => patchOption(fi, oi, patch)}
          onAddOption={() => addOption(fi)}
          onRemoveOption={(oi) => removeOption(fi, oi)}
          onMoveOption={(oi, dir) => moveOption(fi, oi, dir)}
          onMoveField={(dir) => moveField(fi, dir)}
        />
      ))}
    </main>
  );
};

const FieldCard = ({
  field,
  index,
  total,
  onPatch,
  onPatchOption,
  onAddOption,
  onRemoveOption,
  onMoveOption,
  onMoveField,
}) => {
  const isRange = field.presentation === "range";
  const isChoice =
    field.presentation === "grid" || field.presentation === "searchList";
  const optionCount = (field.options || []).length;
  const isLong = optionCount > 8;

  // Long option lists (countries, nationalities…) start collapsed.
  const [open, setOpen] = useState(!isLong);
  const [query, setQuery] = useState("");

  const q = query.trim().toLowerCase();
  const disabled = field.enabled === false;

  return (
    <div className="card mb-3">
      <div className="card-body" style={{ opacity: disabled ? 0.7 : 1 }}>
        {/* ---- header row ---- */}
        <div className="d-flex flex-wrap align-items-start justify-content-between gap-3">
          <div
            className="d-flex align-items-center gap-2"
            style={{ flex: "1 1 320px", minWidth: 260 }}
          >
            <div className="btn-group-vertical btn-group-sm" role="group">
              <button
                className="btn btn-light border"
                disabled={index === 0}
                onClick={() => onMoveField(-1)}
                title="Move up"
              >
                ▲
              </button>
              <button
                className="btn btn-light border"
                disabled={index === total - 1}
                onClick={() => onMoveField(1)}
                title="Move down"
              >
                ▼
              </button>
            </div>
            <div style={{ flex: 1 }}>
              <label
                className="text-muted mb-1 d-block"
                style={{ fontSize: 11 }}
              >
                Field label
              </label>
              <input
                className="form-control form-control-sm fw-bold"
                value={field.label || ""}
                onChange={(e) => onPatch({ label: e.target.value })}
              />
              <span className="badge bg-light text-secondary border mt-2">
                {field.key} · {field.presentation}
                {isChoice ? ` · ${optionCount} options` : ""}
              </span>
            </div>
          </div>

          <div className="d-flex flex-column gap-2" style={{ minWidth: 210 }}>
            <div className="d-flex flex-wrap gap-3">
              <div className="form-check m-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`en-${field.key}`}
                  checked={field.enabled !== false}
                  onChange={(e) => onPatch({ enabled: e.target.checked })}
                />
                <label className="form-check-label small" htmlFor={`en-${field.key}`}>
                  Enabled
                </label>
              </div>
              <div className="form-check m-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`inf-${field.key}`}
                  checked={field.inFilter !== false}
                  onChange={(e) => onPatch({ inFilter: e.target.checked })}
                />
                <label className="form-check-label small" htmlFor={`inf-${field.key}`}>
                  In filter
                </label>
              </div>
              <div className="form-check m-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`inp-${field.key}`}
                  checked={field.inProfile !== false}
                  onChange={(e) => onPatch({ inProfile: e.target.checked })}
                />
                <label className="form-check-label small" htmlFor={`inp-${field.key}`}>
                  In profile
                </label>
              </div>
            </div>
            {isChoice && (
              <div className="d-flex gap-3">
                <div className="form-check m-0">
                  <input
                    className="form-check-input"
                    type="radio"
                    name={`sel-${field.key}`}
                    id={`single-${field.key}`}
                    checked={!field.multiSelect}
                    onChange={() => onPatch({ multiSelect: false })}
                  />
                  <label className="form-check-label small" htmlFor={`single-${field.key}`}>
                    Single select
                  </label>
                </div>
                <div className="form-check m-0">
                  <input
                    className="form-check-input"
                    type="radio"
                    name={`sel-${field.key}`}
                    id={`multi-${field.key}`}
                    checked={!!field.multiSelect}
                    onChange={() => onPatch({ multiSelect: true })}
                  />
                  <label className="form-check-label small" htmlFor={`multi-${field.key}`}>
                    Multi select
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ---- range inputs ---- */}
        {isRange && <RangeEditor field={field} onPatch={onPatch} />}

        {/* ---- collapsible options ---- */}
        {isChoice && (
          <div className="mt-3 border-top pt-3">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <button
                className="btn btn-sm btn-link text-decoration-none p-0 fw-semibold"
                onClick={() => setOpen((v) => !v)}
                style={{ color: "#043152" }}
              >
                {open ? "▾" : "▸"} Options ({optionCount})
              </button>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => {
                  setOpen(true);
                  onAddOption();
                }}
              >
                + Add option
              </button>
            </div>

            {open && (
              <div className="mt-2">
                <div className="text-muted mb-2" style={{ fontSize: 12 }}>
                  <b>Label</b> is shown to members; <b>value</b> is what’s
                  stored/matched (change with care).
                </div>

                {isLong && (
                  <input
                    className="form-control form-control-sm mb-2"
                    placeholder={`Filter ${optionCount} options…`}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                )}

                <div
                  style={{
                    maxHeight: 340,
                    overflowY: "auto",
                    paddingRight: 4,
                  }}
                >
                  {(field.options || []).map((o, oi) => {
                    if (
                      q &&
                      !`${o.label} ${o.value}`.toLowerCase().includes(q)
                    ) {
                      return null;
                    }
                    return (
                      <div
                        key={oi}
                        className="d-flex gap-2 align-items-center mb-2"
                      >
                        <input
                          className="form-control form-control-sm"
                          placeholder="Label"
                          value={o.label || ""}
                          onChange={(e) =>
                            onPatchOption(oi, { label: e.target.value })
                          }
                        />
                        <input
                          className="form-control form-control-sm text-muted"
                          placeholder="Value (stored)"
                          value={o.value || ""}
                          onChange={(e) =>
                            onPatchOption(oi, { value: e.target.value })
                          }
                        />
                        <button
                          className="btn btn-sm btn-light border"
                          disabled={!!q || oi === 0}
                          title={q ? "Clear filter to reorder" : "Move up"}
                          onClick={() => onMoveOption(oi, -1)}
                        >
                          ▲
                        </button>
                        <button
                          className="btn btn-sm btn-light border"
                          disabled={!!q || oi === optionCount - 1}
                          title={q ? "Clear filter to reorder" : "Move down"}
                          onClick={() => onMoveOption(oi, 1)}
                        >
                          ▼
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          title="Remove"
                          onClick={() => onRemoveOption(oi)}
                        >
                          ✕
                        </button>
                      </div>
                    );
                  })}
                  {optionCount === 0 && (
                    <div className="text-muted small fst-italic">
                      No options yet — add one above.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterSettings;
